import {
  Autocomplete,
  Card,
  Divider,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { Loading, Notify } from 'notiflix';
import { ENDPOINT } from 'constants';
import { useSelector } from 'react-redux';
import usePermission from 'hooks/usePermission';

// Customer-specific missed-payment rules. A customer with a rule here is judged by it
// instead of the global policy; any field left empty uses the global value. A customer
// without a rule follows the global policy.

const API = `${ENDPOINT}api/v1/admin/missed-payment-settings`;
const MODES = [
  { value: 'global', label: 'Follow the global switch' },
  { value: 'on', label: 'Always apply to this customer' },
  { value: 'off', label: 'Never pause / hold / cancel this customer' },
];
const EMPTY_FORM = { mode: 'global', pauseAfterDays: '', onHoldAfterDays: '', cancelAfterDays: '', note: '' };

const modeOf = (enabled) => (enabled === true ? 'on' : enabled === false ? 'off' : 'global');
const enabledOf = (mode) => (mode === 'on' ? true : mode === 'off' ? false : null);

const CustomerRules = ({ globalPolicy, refreshKey }) => {
  const { user } = useSelector((state) => state.auth);
  const canUpdate = usePermission('settings', 'update');
  const authHeader = { headers: { Authorization: `Bearer ${user?.accessToken}` } };

  const [rules, setRules] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadRules = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/users`, authHeader);
      setRules(data.data.rules || []);
    } catch (error) {
      console.error(error);
      Notify.failure('Failed to load customer rules');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.accessToken]);

  useEffect(() => {
    loadRules();
  }, [loadRules, refreshKey]);

  // customer search (email or name), after a short pause in typing
  useEffect(() => {
    if (query.trim().length < 2) {
      setOptions([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/customers`, { ...authHeader, params: { q: query.trim() } });
        setOptions(data.data.customers || []);
      } catch (error) {
        console.error(error);
      }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const reset = () => {
    setCustomer(null);
    setForm(EMPTY_FORM);
    setQuery('');
  };

  const edit = (rule) => {
    setCustomer({ userId: rule.userId, email: rule.email, name: rule.name });
    setForm({
      mode: modeOf(rule.enabled),
      pauseAfterDays: rule.pauseAfterDays ?? '',
      onHoldAfterDays: rule.onHoldAfterDays ?? '',
      cancelAfterDays: rule.cancelAfterDays ?? '',
      note: rule.note || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const save = async () => {
    if (!customer) return;
    try {
      setSaving(true);
      await axios.put(
        `${API}/users/${customer.userId}`,
        {
          enabled: enabledOf(form.mode),
          pauseAfterDays: form.pauseAfterDays === '' ? null : Number(form.pauseAfterDays),
          onHoldAfterDays: form.onHoldAfterDays === '' ? null : Number(form.onHoldAfterDays),
          cancelAfterDays: form.cancelAfterDays === '' ? null : Number(form.cancelAfterDays),
          note: form.note,
        },
        authHeader,
      );
      Notify.success('Customer rule saved');
      reset();
      await loadRules();
    } catch (error) {
      console.error(error);
      Notify.failure(error.response?.data?.message || 'Failed to save the customer rule');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rule) => {
    try {
      Loading.circle('Removing...');
      await axios.delete(
        `${API}/users/${rule.userId}?label=${encodeURIComponent(rule.name || rule.email || '')}`,
        authHeader,
      );
      Notify.success('Rule removed — the global policy applies to this customer again');
      if (customer?.userId === rule.userId) reset();
      await loadRules();
    } catch (error) {
      console.error(error);
      Notify.failure(error.response?.data?.message || 'Failed to remove the rule');
    } finally {
      Loading.remove();
    }
  };

  const days = (rule, field) =>
    rule[field] === null || rule[field] === undefined ? `${rule.effective[field]} (global)` : rule[field];
  const globalHint = (field) => (globalPolicy ? `Global: ${globalPolicy[field]}` : '');
  const switchLabel = (rule) =>
    rule.enabled === true ? 'Always on' : rule.enabled === false ? 'Never' : `Global (${rule.effective.enabled ? 'on' : 'off'})`;

  return (
    <Card sx={{ p: 3, mt: 3 }}>
      <MDTypography variant="h5" mb={1}>
        Customer-specific rules
      </MDTypography>
      <MDTypography variant="caption" color="secondary">
        Give one customer their own days. A customer with a rule below is judged by it instead of the
        global policy; a day left empty uses the global value. Customers without a rule follow the global
        policy. Use &quot;Never&quot; to keep a customer out of automatic pause, hold and cancel, or
        &quot;Always apply&quot; to try the policy on a single customer while the global switch is off.
      </MDTypography>
      <Divider sx={{ my: 2 }} />

      {canUpdate && (
        <MDBox sx={{ maxWidth: 650 }}>
          <Autocomplete
            options={options}
            value={customer}
            onChange={(_, value) => setCustomer(value)}
            onInputChange={(_, value) => setQuery(value)}
            getOptionLabel={(o) => (o ? `${o.email}${o.name ? ` (${o.name})` : ''}` : '')}
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            filterOptions={(x) => x}
            noOptionsText={query.trim().length < 2 ? 'Type at least 2 letters of the email or name' : 'No customer found'}
            renderInput={(params) => (
              <TextField {...params} label="Customer (search by email or name)" margin="normal" fullWidth InputLabelProps={{ shrink: true }} />
            )}
          />
          <TextField
            select
            fullWidth
            margin="normal"
            label="Automatic pause / hold / cancel"
            value={form.mode}
            onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputBase-root': { height: 44 } }}
          >
            {MODES.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>
          {[
            ['pauseAfterDays', 'Pause after (days)'],
            ['onHoldAfterDays', 'Put on hold after (days)'],
            ['cancelAfterDays', 'Cancel after (days)'],
          ].map(([field, label]) => (
            <TextField
              key={field}
              type="number"
              label={label}
              value={form[field]}
              onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
              fullWidth
              margin="normal"
              inputProps={{ min: 1, max: 365 }}
              InputLabelProps={{ shrink: true }}
              placeholder={globalHint(field)}
              helperText={`Empty = use the global value. ${globalHint(field)}`}
            />
          ))}
          <TextField
            label="Note (why this customer is special)"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            fullWidth
            margin="normal"
            inputProps={{ maxLength: 255 }}
            InputLabelProps={{ shrink: true }}
          />
          <MDBox mt={2} display="flex" gap={2}>
            <MDButton variant="gradient" color="info" disabled={saving || !customer} onClick={save}>
              {saving ? 'Saving...' : 'Save customer rule'}
            </MDButton>
            {(customer || form !== EMPTY_FORM) && (
              <MDButton variant="outlined" color="secondary" onClick={reset}>
                Clear
              </MDButton>
            )}
          </MDBox>
        </MDBox>
      )}

      <MDTypography variant="h6" mt={4} mb={1}>
        Customers with a special rule ({rules.length})
      </MDTypography>
      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          overflowX: 'auto',
        }}>
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            // Columns need at least this much room to stay readable; below it
            // the container scrolls horizontally instead of squeezing them.
            minWidth: 820,
          }}>
          {/* The dashboard theme makes all table heads `display: block`, which
              disconnects header column sizing from the body. Reset it locally
              so columns line up, matching the account-permissions matrix. */}
          <TableHead sx={{ display: 'table-header-group', p: 0, borderRadius: 0 }}>
            <TableRow>
              <TableCell sx={{ width: 220, minWidth: 220 }}>Customer</TableCell>
              <TableCell sx={{ width: 140, minWidth: 140 }}>Automatic</TableCell>
              <TableCell sx={{ width: 110, minWidth: 110 }}>Pause</TableCell>
              <TableCell sx={{ width: 110, minWidth: 110 }}>On hold</TableCell>
              <TableCell sx={{ width: 110, minWidth: 110 }}>Cancel</TableCell>
              <TableCell sx={{ width: 150, minWidth: 150 }}>Note</TableCell>
              {canUpdate && <TableCell sx={{ width: 140, minWidth: 140 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>No customer has a special rule. Everyone follows the global policy.</TableCell>
              </TableRow>
            )}
            {rules.map((rule) => (
              <TableRow key={rule.userId}>
                <TableCell sx={{ width: 220, minWidth: 220 }}>
                  {rule.email || rule.userId}
                  {rule.name ? ` (${rule.name})` : ''}
                </TableCell>
                <TableCell sx={{ width: 140, minWidth: 140 }}>{switchLabel(rule)}</TableCell>
                <TableCell sx={{ width: 110, minWidth: 110 }}>{days(rule, 'pauseAfterDays')}</TableCell>
                <TableCell sx={{ width: 110, minWidth: 110 }}>{days(rule, 'onHoldAfterDays')}</TableCell>
                <TableCell sx={{ width: 110, minWidth: 110 }}>{days(rule, 'cancelAfterDays')}</TableCell>
                <TableCell sx={{ width: 150, minWidth: 150 }}>{rule.note || ''}</TableCell>
                {canUpdate && (
                  <TableCell align="right" sx={{ width: 140, minWidth: 140, whiteSpace: 'nowrap' }}>
                    <MDButton size="small" variant="text" color="info" onClick={() => edit(rule)}>
                      Edit
                    </MDButton>
                    <MDButton size="small" variant="text" color="error" onClick={() => remove(rule)}>
                      Remove
                    </MDButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

CustomerRules.propTypes = {
  globalPolicy: PropTypes.shape({
    pauseAfterDays: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onHoldAfterDays: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    cancelAfterDays: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  refreshKey: PropTypes.number,
};

CustomerRules.defaultProps = { globalPolicy: null, refreshKey: 0 };

export default CustomerRules;
